import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  FormGroup,
  Input,
} from "reactstrap";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getWorkOrderOptions } from "features/WorkOrders/services/WorkOrderService";
import { WorkOrderStatus } from "features/WorkOrders/constants/work-order.constants";
import {
  WorkOrderOption,
  WorkOrderSingleValue,
} from "features/WorkOrders/components/WorkOrderSelectComponents";
import { getPettyWorkOptions } from "features/PettyWorks/services/PettyWorkService";
import { PettyWorkStatus } from "features/PettyWorks/constants/petty-work.constants";
import {
  PettyWorkOption,
  PettyWorkSingleValue,
} from "features/PettyWorks/components/PettyWorkSelectComponents";
import type { SelectOption } from "types/api.types";

type SourceType = "WORK_ORDER" | "PETTY_WORK";

export default function MaterialRequestNewModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [sourceType, setSourceType] = useState<SourceType>("WORK_ORDER");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const handleSourceTypeChange = (type: SourceType) => {
    setSourceType(type);
    setSelectedId(null);
  };

  const handleContinue = () => {
    if (!selectedId) return;
    const path =
      sourceType === "WORK_ORDER"
        ? `/inventory-management/material-requests/new/from-work-order/${selectedId}`
        : `/inventory-management/material-requests/new/from-petty-work/${selectedId}`;
    onClose();
    navigate(path);
  };

  const handleClose = () => {
    setSelectedId(null);
    setSourceType("WORK_ORDER");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>New material request</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label className="form-label">Source type</Label>
          <div className="d-flex gap-3">
            <div className="form-check">
              <Input
                type="radio"
                id="mr-source-wo"
                name="mr-source-type"
                className="form-check-input"
                checked={sourceType === "WORK_ORDER"}
                onChange={() => handleSourceTypeChange("WORK_ORDER")}
              />
              <Label className="form-check-label" for="mr-source-wo">
                Work order
              </Label>
            </div>
            <div className="form-check">
              <Input
                type="radio"
                id="mr-source-pw"
                name="mr-source-type"
                className="form-check-input"
                checked={sourceType === "PETTY_WORK"}
                onChange={() => handleSourceTypeChange("PETTY_WORK")}
              />
              <Label className="form-check-label" for="mr-source-pw">
                Petty work
              </Label>
            </div>
          </div>
        </FormGroup>
        <FormGroup className="mb-0">
          <Label className="form-label">
            {sourceType === "WORK_ORDER" ? "Work order" : "Petty work"}
            <span className="text-danger ms-1">*</span>
          </Label>
          {sourceType === "WORK_ORDER" ? (
            <AsyncPaginatedSelect
              key="wo"
              fetchOptions={getWorkOrderOptions}
              components={{
                Option: WorkOrderOption,
                SingleValue: WorkOrderSingleValue,
              }}
              requestParams={{
                status: [WorkOrderStatus.NEW, WorkOrderStatus.IN_PROGRESS, WorkOrderStatus.ON_HOLD].join(","),
              }}
              placeholder="Search work orders…"
              value={selectedId}
              onChange={(opt: SelectOption | SelectOption[] | null) => {
                const o = Array.isArray(opt) ? opt[0] : opt;
                setSelectedId(o?.value ?? null);
              }}
            />
          ) : (
            <AsyncPaginatedSelect
              key="pw"
              fetchOptions={getPettyWorkOptions}
              components={{
                Option: PettyWorkOption,
                SingleValue: PettyWorkSingleValue,
              }}
              requestParams={{ status: [PettyWorkStatus.NEW, PettyWorkStatus.IN_PROGRESS].join(",") }}
              placeholder="Search petty works…"
              value={selectedId}
              onChange={(opt: SelectOption | SelectOption[] | null) => {
                const o = Array.isArray(opt) ? opt[0] : opt;
                setSelectedId(o?.value ?? null);
              }}
            />
          )}
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="light" className="border" onClick={handleClose}>
          Cancel
        </Button>
        <Button color="primary" disabled={!selectedId} onClick={handleContinue}>
          Continue
          <i className="ri-arrow-right-line ms-1 align-bottom" />
        </Button>
      </ModalFooter>
    </Modal>
  );
}
